import { BONUS_REASON_ACCOUNT_OPT_OUT, BONUS_REASON_NOT_IN_WALLET_COHORT, MICROS_PER_UNIT } from './Wallet';

/**
 * Bônus de recarga (CU-86akn0zp6): `GET/PUT /admin/wallet/bonus-tiers` e
 * `GET /recharges/bonus-quote`.
 *
 * ## Uma tabela só, por VALOR
 *
 * A faixa é "a partir de R$ X, dá R$ Y de bônus" e não tem produto: o bônus cai
 * na carteira em dinheiro, e dinheiro não tem produto. Uma tabela por produto
 * obrigaria a escolher a faixa antes de saber em que o cliente vai gastar.
 *
 * ## A tabela é substituída inteira
 *
 * Ao contrário das rotas de preço, esta é um `PUT` que troca a lista toda —
 * ela é uma configuração (`WALLET_BONUS_TIERS`), não uma série de vigências.
 * O histórico de quem mexeu vem da auditoria de configuração, e é por isso que
 * a tela abre o mesmo modal das configurações globais.
 *
 * ## Números
 *
 * `min_total_micros` e `bonus_micros` são **inteiros em micros** (1 BRL =
 * 1.000.000), como todo dinheiro da carteira.
 */

export interface BonusTier {
  /** A partir de quanto a faixa vale, em micros, inclusive. */
  min_total_micros: number;
  bonus_micros: number;
}

/** Envelope das duas pontas da rota: `{ data: [...] }`, em ordem crescente. */
export interface BonusTierList {
  data: BonusTier[];
}

/**
 * Por que a cotação deu o bônus que deu.
 *
 * `no_tier` é "o valor não alcançou nenhuma faixa"; os dois `*_excluded` são as
 * configurações de acúmulo desligadas; `wallet_unavailable` é a carteira fora —
 * e nesse caso o zero **não** é uma resposta sobre a faixa.
 */
export type BonusQuoteReason =
  | 'ok'
  | 'no_tier'
  | 'subscriber_excluded'
  | 'account_price_excluded'
  | 'wallet_unavailable'
  | typeof BONUS_REASON_ACCOUNT_OPT_OUT
  | typeof BONUS_REASON_NOT_IN_WALLET_COHORT;

export interface BonusQuote {
  paid_micros: number;
  bonus_micros: number;
  /** A faixa que venceu, ou `null` quando nenhuma se aplicou. */
  tier: BonusTier | null;
  reason: BonusQuoteReason | string;
  tiers: BonusTier[];
  min_total_micros: number;
  pricing_tier: string;
  /** `false` = a conta tem "Recebe bônus de recarga" desligada (CU-17tm466tzkz). */
  bonus_eligible?: boolean;
}

export const BONUS_REASON_LABELS: { [reason: string]: string } = {
  ok: 'Bônus da faixa aplicado.',
  no_tier: 'O valor não alcança nenhuma faixa de bônus.',
  subscriber_excluded: 'Este cliente tem preço de assinante, e o bônus não acumula com ele.',
  account_price_excluded: 'Este cliente tem preço de conta (contrato), e o bônus não acumula com ele.',
  wallet_unavailable: 'A carteira não respondeu — não dá para dizer se haveria bônus.',
  [BONUS_REASON_ACCOUNT_OPT_OUT]: 'Esta conta está configurada para não receber bônus de recarga.',
  [BONUS_REASON_NOT_IN_WALLET_COHORT]: 'Cliente fora da carteira em R$: recarga por quantidade não recebe bônus de valor.',
};

export function bonusReasonLabel(reason: string | null | undefined): string {
  if (!reason) {
    return '';
  }

  return BONUS_REASON_LABELS[reason] || reason;
}

/** A carteira não respondeu: o zero na tela não é uma resposta sobre a faixa. */
export function isBonusUnknown(quote: BonusQuote | null): boolean {
  return !!quote && quote.reason === 'wallet_unavailable';
}

// =============================================================================
// Chaves do catálogo de configuração
// =============================================================================

/**
 * As chaves que esta tela lê e grava pelo catálogo de configurações globais.
 *
 * Elas não são inventadas aqui: o backend declara o catálogo como allowlist, e a
 * tela só edita valor. Por isso a tela carrega o catálogo e procura a chave em
 * vez de montar um `GlobalConfiguration` de mentira — se a chave não existir no
 * ambiente, é melhor a tela dizer isso do que gravar num nome que ninguém lê.
 */
export const BONUS_TIERS_KEY = 'WALLET_BONUS_TIERS';
export const BONUS_WITH_SUBSCRIBER_PRICE_KEY = 'WALLET_BONUS_WITH_SUBSCRIBER_PRICE';
export const BONUS_WITH_ACCOUNT_PRICE_KEY = 'WALLET_BONUS_WITH_ACCOUNT_PRICE';
export const RECHARGE_MIN_MICROS_KEY = 'WALLET_RECHARGE_MIN_MICROS';

// =============================================================================
// Regras da tabela de faixas
// =============================================================================

/**
 * Menor valor que uma faixa pode exigir: R$ 1,00.
 *
 * Uma faixa "a partir de R$ 0,00" daria bônus em toda recarga, inclusive na de
 * um centavo — e a tabela deixaria de ser uma escada para virar um desconto
 * geral, sem ninguém ter decidido isso.
 */
export const BONUS_MIN_TOTAL_MICROS = MICROS_PER_UNIT;

/** Faixas em ordem crescente de `min_total_micros`, que é como elas se leem. */
export function sortBonusTiers(tiers: BonusTier[]): BonusTier[] {
  return (tiers || []).slice().sort((a, b) => a.min_total_micros - b.min_total_micros);
}

/**
 * A faixa que vale para um valor: a maior cujo `min_total_micros` o valor
 * alcança.
 *
 * É a mesma conta que o BFF faz, repetida aqui **só para o simulador da tela** —
 * quem responde pelo bônus de verdade é `bonus-quote`, que enxerga as
 * configurações de acúmulo e o preço do cliente. O simulador responde "a faixa é
 * esta", não "este cliente ganharia isto".
 */
export function resolveBonusTier(tiers: BonusTier[], totalMicros: number | null): BonusTier | null {
  if (totalMicros === null || totalMicros === undefined || !isFinite(totalMicros)) {
    return null;
  }

  return sortBonusTiers(tiers)
    .filter((tier) => totalMicros >= tier.min_total_micros)
    .pop() || null;
}

export interface BonusTierProblem {
  /** Índice da linha na ordem em que ela está na tela. */
  index: number;
  message: string;
}

/**
 * Os problemas da tabela inteira, linha a linha.
 *
 * Validar aqui e não só no campo é o que pega o erro que só existe ENTRE as
 * linhas: duas faixas com o mesmo "a partir de" não têm vencedora definida, e o
 * bônus de uma recarga passaria a depender da ordem em que as linhas foram
 * gravadas.
 */
export function validateBonusTiers(tiers: Array<Partial<BonusTier>>): BonusTierProblem[] {
  const problems: BonusTierProblem[] = [];
  const seen: { [micros: number]: number } = {};

  (tiers || []).forEach((tier, index) => {
    const min = tier.min_total_micros;
    const bonus = tier.bonus_micros;

    if (min === null || min === undefined || !isFinite(min)) {
      problems.push({ index, message: 'Informe o valor a partir do qual a faixa vale.' });
    } else if (min < BONUS_MIN_TOTAL_MICROS) {
      problems.push({ index, message: 'A faixa precisa começar em R$ 1,00 ou mais.' });
    } else if (seen[min] !== undefined) {
      problems.push({
        index,
        message: `Já existe uma faixa começando neste valor (linha ${seen[min] + 1}).`,
      });
    } else {
      seen[min] = index;
    }

    if (bonus === null || bonus === undefined || !isFinite(bonus)) {
      problems.push({ index, message: 'Informe o bônus da faixa.' });
    } else if (bonus < 0) {
      problems.push({ index, message: 'O bônus não pode ser negativo.' });
    }
  });

  return problems;
}
